import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Snackbar,
  Alert,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import InfoIcon      from "@mui/icons-material/Info";
import Tooltip       from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import ErrorIcon        from "@mui/icons-material/Error";

const UserFormDialog = ({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  mode,
  availableRoles = [],
  showAssociatedAccount = true,
  hideRoleSelection = false,
}) => {
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  // Member preview. keyed by account code
  const [memberPreviews, setMemberPreviews] = useState({}); // { code: { status:'loading'|'found'|'not_found', data } }

  const lookupMember = useCallback(async (code) => {
    if (!code || memberPreviews[code]) return;
    setMemberPreviews(p => ({ ...p, [code]: { status: 'loading' } }));
    try {
      const r = await api.get(`/member-lookup/${encodeURIComponent(code)}`);
      setMemberPreviews(p => ({ ...p, [code]: { status: 'found', data: r.data } }));
    } catch {
      setMemberPreviews(p => ({ ...p, [code]: { status: 'not_found' } }));
    }
  }, [memberPreviews]);

  useEffect(() => {
    if (!open) {
      setConfirmPassword("");
      setPasswordError("");
      setOpenSnackbar(false);
      setMemberPreviews({});
    } else {
      // In edit mode, look up existing associated accounts immediately
      const codes = formData.AssociatedAccounts || [];
      codes.forEach(code => {
        if (code && !memberPreviews[code]) {
          setMemberPreviews(p => ({ ...p, [code]: { status: 'loading' } }));
          api.get(`/member-lookup/${encodeURIComponent(code)}`)
            .then(r => setMemberPreviews(p => ({ ...p, [code]: { status: 'found', data: r.data } })))
            .catch(() => setMemberPreviews(p => ({ ...p, [code]: { status: 'not_found' } })));
        }
      });
    }
  }, [open]); // eslint-disable-line

  const roles = Array.isArray(formData.Roles) ? formData.Roles : [];

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const validatePasswords = () => {
    if ((formData.Password || "").length < 8) {
      setPasswordError("Password must be at least 8 characters long.");
      return false;
    }

    if (formData.Password !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return false;
    }

    setPasswordError("");
    return true;
  };

  const handleSubmit = () => {
    if (mode === "add" && !validatePasswords()) return;

    const normalizedData = {
      ...formData,
      AssociatedAccounts: Array.isArray(formData.AssociatedAccounts)
        ? formData.AssociatedAccounts.map((acc) => acc.trim()).filter(Boolean)
        : [],
    };

    onSubmit(normalizedData);
    setOpenSnackbar(true);
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          {mode === "add" ? "Add New User" : "Edit User"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            {mode === "add"
              ? "Enter the user details below to create a new account."
              : "Update the user details below."}
          </Typography>

          <TextField
            label="Username"
            fullWidth
            value={formData.Username || ""}
            onChange={handleChange("Username")}
            sx={fieldSx}
          />

          {showAssociatedAccount && (
            <Box
              sx={{
                mt: 2,
                px: 1.25,
                py: 1.25,
                borderRadius: "12px",
                backgroundColor: "rgba(47,99,173,0.04)",
                border: "1px solid rgba(47,99,173,0.08)",
              }}
            >
              <Autocomplete
                multiple
                freeSolo
                filterSelectedOptions
                options={[]}
                value={formData.AssociatedAccounts || []}
                onChange={(e, newValue) => {
                  const codes = newValue.map(a => a.trim()).filter(Boolean);
                  setFormData({ ...formData, AssociatedAccounts: codes });
                  // Look up any new codes
                  codes.forEach(code => lookupMember(code));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: memberPreviews[option]?.status === 'not_found'
                          ? "rgba(220,38,38,0.10)" : "rgba(47,99,173,0.10)",
                        color: memberPreviews[option]?.status === 'not_found'
                          ? "#dc2626" : "#2f63ad",
                        fontWeight: 600,
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <span>Associated Accounts</span>
                        <Tooltip title="Press Enter after typing each member code. The member's name will appear below to confirm.">
                          <InfoIcon sx={{ fontSize: 16, color: "gray" }} />
                        </Tooltip>
                      </Box>
                    }
                    fullWidth
                    helperText="Type a member code and press Enter. the member name will load automatically"
                    sx={{
                      "& .MuiOutlinedInput-root": { backgroundColor: "rgba(255,255,255,0.96)", borderRadius: "10px" },
                      "& .MuiFormHelperText-root": { color: "#6b778c", fontWeight: 500 },
                    }}
                  />
                )}
              />

              {/* Member name preview for each associated account */}
              {(formData.AssociatedAccounts || []).length > 0 && (
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {(formData.AssociatedAccounts || []).map(code => {
                    const preview = memberPreviews[code];
                    if (!preview) return (
                      <Box key={code} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={14} />
                        <Typography variant="caption" sx={{ color: "#6b778c" }}>Looking up {code}…</Typography>
                      </Box>
                    );
                    if (preview.status === 'loading') return (
                      <Box key={code} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={14} />
                        <Typography variant="caption" sx={{ color: "#6b778c" }}>Looking up {code}…</Typography>
                      </Box>
                    );
                    if (preview.status === 'not_found') return (
                      <Box key={code} sx={{ display: "flex", alignItems: "center", gap: 0.75, p: 1, borderRadius: "8px", backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.18)" }}>
                        <ErrorIcon sx={{ fontSize: 16, color: "#dc2626" }} />
                        <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 600 }}>
                          Code "{code}". member not found. Please verify the code.
                        </Typography>
                      </Box>
                    );
                    const m = preview.data;
                    return (
                      <Box key={code} sx={{ display: "flex", alignItems: "center", gap: 0.75, p: 1, borderRadius: "8px", backgroundColor: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.18)" }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#15803d", display: "block" }}>
                            {m.first_name} {m.last_name} ({code})
                          </Typography>
                          {m.email && <Typography variant="caption" sx={{ color: "#6b778c", display: "block" }}>{m.email}{m.phone ? ` · ${m.phone}` : ""}</Typography>}
                          {m.address && <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block" }}>{m.address}</Typography>}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {mode === "add" && (
            <>
              <TextField
                label="Password"
                type="password"
                fullWidth
                required
                value={formData.Password || ""}
                onChange={handleChange("Password")}
                error={!!passwordError}
                sx={fieldSx}
              />

              <TextField
                label="Confirm Password"
                type="password"
                fullWidth
                required
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                error={!!passwordError}
                helperText={passwordError}
                sx={fieldSx}
              />
            </>
          )}

          <TextField
            label="First Name"
            fullWidth
            value={formData.Name || ""}
            onChange={handleChange("Name")}
            sx={fieldSx}
          />

          <TextField
            label="Last Name"
            fullWidth
            value={formData.Lastname || ""}
            onChange={handleChange("Lastname")}
            sx={fieldSx}
          />

          <TextField
            label="Email"
            fullWidth
            value={formData.Email || ""}
            onChange={handleChange("Email")}
            sx={fieldSx}
          />

          {!hideRoleSelection && (
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={roles}
                onChange={(e) =>
                  setFormData({ ...formData, Roles: e.target.value })
                }
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) =>
                  availableRoles
                    .filter((role) => selected.includes(role.RoleID))
                    .map((role) => role.RoleName)
                    .join(", ")
                }
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.RoleID} value={role.RoleID}>
                    <Checkbox checked={roles.includes(role.RoleID)} />
                    <ListItemText primary={role.RoleName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box
            sx={{
              mt: 2,
              px: 1.25,
              py: 1.1,
              borderRadius: "12px",
              backgroundColor: "rgba(47,99,173,0.04)",
              border: "1px solid rgba(47,99,173,0.08)",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.SendInvite || false}
                  onChange={(e) =>
                    setFormData({ ...formData, SendInvite: e.target.checked })
                  }
                />
              }
              label="Send invitation email"
              sx={{ m: 0 }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button onClick={onClose} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>

          <Button onClick={handleSubmit} variant="contained" sx={primaryButtonSx}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {mode === "add"
            ? "User added successfully!"
            : "User updated successfully!"}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserFormDialog;
